import { useCallback, useEffect, useMemo, useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { css } from '@codemirror/lang-css';
import { html } from '@codemirror/lang-html';
import { json } from '@codemirror/lang-json';
import { EditorView } from '@codemirror/view';
import { defaultHighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { oneDarkHighlightStyle } from '@codemirror/theme-one-dark';
import { showMinimap } from '@replit/codemirror-minimap';
import { useTheme } from '../../lib/theme';
import { api } from '../../lib/api';

/**
 * The workspace code editor (spec 5b.8, reworked): the explorer lives in the rail, and the SELECTED
 * file opens here — where the preview normally sits — in a real editor rather than a bare textarea
 * squeezed into a 380px column. Syntax highlighting, line numbers, Cmd/Ctrl+S, and compile feedback
 * on save.
 *
 * Free users still get the full reading experience; only writing is Pro (enforced by the API — the
 * read-only state here is presentation, not the security boundary).
 */

export interface CodeFileRef {
  id: string;
  path: string;
  content: string | null;
}

function languageFor(path: string) {
  if (/\.(jsx?|tsx?|mjs)$/i.test(path)) return javascript({ jsx: true });
  if (/\.css$/i.test(path)) return css();
  if (/\.html?$/i.test(path)) return html();
  if (/\.json$/i.test(path)) return json();
  return javascript(); // README and friends: harmless default
}

export function CodeEditor({
  projectId,
  file,
  editable,
  onSaved,
  onClose,
}: {
  projectId: string;
  file: CodeFileRef;
  editable: boolean;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(file.content ?? '');
  /* the dirty baseline: file.content goes stale the moment a save succeeds, so track our own */
  const [savedContent, setSavedContent] = useState(file.content ?? '');
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [compileErrors, setCompileErrors] = useState<string[]>([]);

  /* switching files replaces the draft wholesale — an unsaved draft for another file must not leak */
  useEffect(() => {
    setDraft(file.content ?? '');
    setSavedContent(file.content ?? '');
    setNote(null);
    setCompileErrors([]);
  }, [file.id, file.content]);

  const dirty = draft !== savedContent;
  const theme = useTheme();
  const extensions = useMemo(
    () => [
      languageFor(file.path),
      /*
       * Only the SYNTAX palette switches here; the editor's surfaces are ours (index.css, driven by
       * the platform tokens). Splitting it this way is deliberate: the wrapper's full-theme prop
       * injects and tears down container styles on every toggle, which left the editor mid-swap —
       * a bare background with the previous theme's text colour. A highlight style is just an
       * extension, so it reconfigures cleanly and nothing else moves.
       */
      syntaxHighlighting(theme === 'dark' ? oneDarkHighlightStyle : defaultHighlightStyle),
      /*
       * Our own theme, in platform tokens, so the editor sits ON the studio surface rather than
       * introducing a third background. It is a real CodeMirror theme (not CSS overrides) because
       * plugins read the active theme to paint themselves — the minimap samples it for its canvas
       * and renders blank without one. `dark` tells CodeMirror which way to bias its own defaults.
       */
      EditorView.theme(
        {
          '&': { backgroundColor: 'rgb(var(--color-surface))', color: 'rgb(var(--color-ink))' },
          /* the last line must never sit flush against the container edge — scroll-past-end room */
          '.cm-content': { caretColor: 'rgb(var(--color-ink))', paddingBottom: '48px' },
          '.cm-gutters': {
            backgroundColor: 'rgb(var(--color-surface))',
            color: 'rgb(var(--color-muted))',
            border: 'none',
          },
          '.cm-activeLine': { backgroundColor: 'rgb(var(--color-ink) / 0.05)' },
          '.cm-activeLineGutter': { backgroundColor: 'rgb(var(--color-ink) / 0.05)', color: 'rgb(var(--color-ink))' },
          '&.cm-focused': { outline: 'none' },
          '.cm-selectionBackground, &.cm-focused .cm-selectionBackground, ::selection': {
            backgroundColor: 'rgb(var(--color-accent) / 0.28)',
          },
          '.cm-cursor, .cm-dropCursor': { borderLeftColor: 'rgb(var(--color-ink))' },
        },
        { dark: theme === 'dark' },
      ),
      /* VS Code-style minimap in the right gutter. Skipped on narrow screens, where its width
         would cost more than the overview is worth. */
      showMinimap.compute([], () =>
        window.innerWidth >= 768
          ? {
              create: () => ({ dom: document.createElement('div') }),
              displayText: 'characters' as const,
              showOverlay: 'always' as const,
            }
          : null,
      ),
    ],
    [file.path, theme],
  );

  const save = useCallback(async () => {
    if (!editable || !dirty || saving) return;
    setSaving(true);
    setNote(null);
    try {
      const res = await api.put<{ compile?: { ok: boolean; errors?: string[] } }>(
        `/box/projects/${projectId}/code/${file.id}`,
        { content: draft },
      );
      if (res.compile && !res.compile.ok) {
        // saved, but the app will not run until this is fixed — say so where the cursor is
        setCompileErrors(res.compile.errors ?? []);
        setNote('Saved, but it does not compile yet');
      } else {
        setCompileErrors([]);
        setNote('Saved');
      }
      setSavedContent(draft);
      onSaved(); // refreshes files + bundle, so the preview shows the edit
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  }, [editable, dirty, saving, projectId, file.id, draft, onSaved]);

  /* Cmd/Ctrl+S — the reflex every editor user has; without this the browser opens a save dialog */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void save();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save]);

  return (
    /* z-30: the zoom footer is z-20 inside the same container and was painting over the editor's
       last lines — the "code cut off at the bottom". While the editor owns the workspace, preview
       chrome must sit underneath it. */
    <div className="absolute inset-0 z-30 flex flex-col bg-surface">
      {/* header: where am I, is it dirty, how do I leave */}
      <div className="flex shrink-0 items-center gap-2 px-3 py-2">
        <span className="min-w-0 flex-1 truncate font-mono text-xs font-semibold text-ink">
          {file.path}
          {dirty && <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-accent align-middle" aria-label="unsaved changes" />}
        </span>
        <span className="truncate text-[11px] text-muted">
          {note ?? (editable ? (dirty ? 'Unsaved — Ctrl+S to save' : '') : 'Read-only — editing code is a Pro feature')}
        </span>
        {editable && (
          <button
            type="button"
            onClick={() => void save()}
            disabled={!dirty || saving}
            className="shrink-0 rounded-lg bg-accent px-3.5 py-1.5 text-xs font-bold text-accent-ink transition hover:brightness-110 disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close the editor"
          title="Back to the preview"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted transition hover:bg-page hover:text-ink"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>

      {/* compile feedback rides above the code, not in a toast that vanishes */}
      {compileErrors.length > 0 && (
        <div className="shrink-0 space-y-0.5 bg-danger/10 px-4 py-2 font-mono text-[11px] text-danger">
          {compileErrors.map((error, i) => (
            <p key={i} className="truncate" title={error}>
              {error}
            </p>
          ))}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto">
        <CodeMirror
          value={draft}
          onChange={setDraft}
          editable={editable}
          /*
           * "none" = the wrapper injects NO theme of its own. Its built-in light theme was painting
           * the editor, gutters and minimap white behind dark code and could not be overridden from
           * `extensions` (it is applied last). With it off, index.css owns every surface from the
           * platform tokens, so the editor re-themes with the rest of the studio and no remount is
           * needed — cursor position and undo history survive a theme toggle.
           */
          theme="none"
          extensions={extensions}
          height="100%"
          style={{ height: '100%', fontSize: 13 }}
          basicSetup={{
            lineNumbers: true,
            foldGutter: true,
            highlightActiveLine: true,
            autocompletion: true,
            bracketMatching: true,
          }}
        />
      </div>
    </div>
  );
}
